import React, { useState, useEffect } from 'react';
import { Calendar, Droplets, Fuel, Activity, TableProperties, Download } from 'lucide-react';

const Reports = () => {
  const [date, setDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    fetch(`/api/reports/daily?date=${date}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => res.json())
      .then(data => {
        if (!data.error) setReport(data);
        setIsLoading(false);
      })
      .catch(err => {
        console.error(err);
        setIsLoading(false);
      });
  }, [date]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Historical Reports</h1>
          <p className="text-slate-500 text-sm mt-1">Review past meter readings, tank dips, and sales volume.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="bg-white px-4 py-2 rounded-xl border border-slate-200 flex items-center gap-2 shadow-sm">
            <Calendar className="w-4 h-4 text-slate-400" />
            <input 
              type="date" 
              className="outline-none text-sm font-medium text-slate-700 bg-transparent"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="p-12 text-center text-slate-500 font-medium">Loading report data...</div>
      ) : !report ? (
        <div className="p-12 text-center text-slate-500 font-medium">Failed to load report.</div>
      ) : (
        <>
          {/* Sales Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="glass-panel p-5 bg-primary-600 text-white shadow-md relative overflow-hidden">
               <div className="absolute right-0 top-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10 pointer-events-none"></div>
               <p className="text-primary-100 text-sm font-semibold mb-1">Total Expected Amount</p>
               <p className="text-3xl font-extrabold tracking-tight">₹{report.summary.totalAmount.toLocaleString()}</p>
            </div>
            {['Petrol', 'Diesel', 'Xtragreen'].map(type => {
               const stats = report.summary[type] || { volume: 0, amount: 0 };
               return (
                 <div key={type} className="glass-panel p-5">
                   <p className="text-sm font-semibold text-slate-500 mb-1">{type} Sale</p>
                   <p className="text-2xl font-bold text-slate-900">{stats.volume.toLocaleString()} L</p>
                   <p className="text-xs font-medium text-slate-400 mt-1">₹{stats.amount.toLocaleString()}</p>
                 </div>
               );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            
            {/* Nozzle Readings Table */}
            <div className="lg:col-span-2 glass-panel overflow-hidden flex flex-col h-[500px]">
              <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
                <Fuel className="w-5 h-5 text-indigo-500" />
                <h2 className="text-lg font-bold text-slate-800">Nozzle Readings</h2>
              </div>
              <div className="overflow-y-auto flex-1 p-0">
                <table className="w-full text-left border-collapse bg-white">
                  <thead className="sticky top-0 bg-slate-50 z-10 shadow-sm">
                    <tr className="text-slate-600 text-xs uppercase tracking-wider">
                      <th className="px-4 py-3 font-semibold border-b border-slate-200">Nozzle</th>
                      <th className="px-4 py-3 font-semibold border-b border-slate-200">Type</th>
                      <th className="px-4 py-3 font-semibold border-b border-slate-200">Opening (Prev)</th>
                      <th className="px-4 py-3 font-semibold border-b border-slate-200">Closing (Curr)</th>
                      <th className="px-4 py-3 font-semibold border-b border-slate-200 text-right">Sale (Liters)</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {report.nozzles.map((noz, idx) => (
                      <tr key={idx} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                        <td className="px-4 py-3 font-semibold text-slate-900">
                          {noz.pump_name} - N{noz.nozzle_number}
                        </td>
                        <td className="px-4 py-3 text-slate-500">{noz.fuel_type}</td>
                        <td className="px-4 py-3 text-slate-600 font-mono">
                           {noz.opening_reading !== null ? noz.opening_reading.toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-slate-600 font-mono">
                           {noz.closing_reading !== null ? noz.closing_reading.toLocaleString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-slate-900">
                           {noz.sale_liters.toLocaleString()} L
                        </td>
                      </tr>
                    ))}
                    {report.nozzles.length === 0 && (
                      <tr>
                        <td colSpan="5" className="p-8 text-center text-slate-500">No readings found for this date.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tank Dips Table */}
            <div className="glass-panel overflow-hidden flex flex-col h-[500px]">
              <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
                <TableProperties className="w-5 h-5 text-emerald-500" />
                <h2 className="text-lg font-bold text-slate-800">Underground Tanks</h2>
              </div>
              <div className="overflow-y-auto flex-1 p-0">
                <table className="w-full text-left border-collapse bg-white">
                  <thead className="sticky top-0 bg-slate-50 z-10 shadow-sm">
                    <tr className="text-slate-600 text-xs uppercase tracking-wider">
                      <th className="px-4 py-3 font-semibold border-b border-slate-200">Tank</th>
                      <th className="px-4 py-3 font-semibold border-b border-slate-200 text-right">Physical Dip</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {report.tanks.map((tank, idx) => (
                      <tr key={idx} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/50">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900">{tank.name}</div>
                          <div className="text-xs text-slate-500">{tank.type}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-slate-900">
                          {tank.dip !== null ? `${tank.dip.toLocaleString()} L` : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
